/**
 * Logger utility for client-side logging
 * Logs are written to browser console
 */

export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
}

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  data?: any;
  error?: Error;
  stack?: string;
}

class Logger {
  private logEntries: LogEntry[] = [];
  private maxLogs = 1000; // Keep last 1000 logs in memory

  private formatMessage(level: LogLevel, message: string, data?: any, error?: Error): string {
    const timestamp = new Date().toISOString();
    const logEntry: LogEntry = {
      level,
      message,
      timestamp,
      data,
      error,
      stack: error?.stack,
    };

    // Add to log entries
    this.logEntries.push(logEntry);
    if (this.logEntries.length > this.maxLogs) {
      this.logEntries.shift(); // Remove oldest
    }

    // Format console message
    const emoji = this.getEmoji(level);
    const prefix = `[${timestamp}] ${emoji} [${level}]`;
    
    return `${prefix} ${message}`;
  }

  private getEmoji(level: LogLevel): string {
    switch (level) {
      case LogLevel.DEBUG:
        return '🔍';
      case LogLevel.INFO:
        return 'ℹ️';
      case LogLevel.WARN:
        return '⚠️';
      case LogLevel.ERROR:
        return '❌';
      default:
        return '📝';
    }
  }

  debug(message: string, data?: any): void {
    const formatted = this.formatMessage(LogLevel.DEBUG, message, data);
    console.debug(formatted, data || '');
  }

  info(message: string, data?: any): void {
    const formatted = this.formatMessage(LogLevel.INFO, message, data);
    console.info(formatted, data || '');
  }

  warn(message: string, data?: any): void {
    const formatted = this.formatMessage(LogLevel.WARN, message, data);
    console.warn(formatted, data || '');
  }

  error(message: string, error?: Error | any, data?: any): void {
    const err = error instanceof Error ? error : new Error(String(error));
    const formatted = this.formatMessage(LogLevel.ERROR, message, data, err);
    console.error(formatted, { error: err, data });
  }

  // Get all logs (for debugging)
  getLogs(): LogEntry[] {
    return [...this.logEntries];
  }

  // Clear logs
  clearLogs(): void {
    this.logEntries = [];
  }

  // Export logs as JSON
  exportLogs(): string {
    return JSON.stringify(this.logEntries, null, 2);
  }
}

// Export singleton instance
export const logger = new Logger();

// Also expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).logger = logger;
}

